import { addToCart, openCartOrGo, goToCart } from '@theme/dert-cart-add';

/**
 * <s-bundle> — shared engine for the product-page bundles (ritual add-on, build-your-set, curated regimens).
 * Selected items are checked checkboxes inside [data-bundle-item]; an optional <select data-bundle-variant>
 * picks the variant. Keeps the running total, progress and button state in sync, enforces data-max,
 * and adds every selected line to the cart in one Standard Actions call (native multi-item form post as fallback).
 */
class SBundle extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('change', () => this.#update(), opts);
    this.addEventListener('submit', (event) => this.#onSubmit(event), opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #min() { return Number.parseInt(this.dataset.min || '1', 10) || 1; }
  get #max() { return Number.parseInt(this.dataset.max || '0', 10) || 0; }

  #items() {
    return [...this.querySelectorAll('[data-bundle-item]')];
  }

  #selected() {
    return this.#items().filter((item) => item.querySelector('[data-bundle-toggle]')?.checked);
  }

  #variantFor(item) {
    const select = item.querySelector('[data-bundle-variant]');
    if (select instanceof HTMLSelectElement) {
      const option = select.selectedOptions[0];
      return { id: select.value, price: Number(option?.dataset.price || 0) };
    }
    const toggle = item.querySelector('[data-bundle-toggle]');
    return { id: toggle?.value || '', price: Number(toggle?.dataset.price || 0) };
  }

  #update() {
    const selected = this.#selected();
    const max = this.#max;

    for (const item of this.#items()) {
      const toggle = item.querySelector('[data-bundle-toggle]');
      const isOn = Boolean(toggle?.checked);
      item.toggleAttribute('data-selected', isOn);
      if (toggle && max && !toggle.hasAttribute('data-locked')) {
        toggle.disabled = !isOn && selected.length >= max;
        item.toggleAttribute('data-disabled', toggle.disabled);
      }
      // Keep the native fallback form in sync: only selected items post.
      const { id } = this.#variantFor(item);
      const hidden = item.querySelector('[data-bundle-line]');
      if (hidden instanceof HTMLInputElement) {
        hidden.value = id;
        hidden.disabled = !isOn;
      }
    }

    const total = selected.reduce((sum, item) => sum + this.#variantFor(item).price, 0);
    for (const totalEl of this.querySelectorAll('[data-bundle-total]')) {
      totalEl.innerHTML = `<bdi data-dert-money="${total}">${this.#formatMoney(total)}</bdi>`;
    }

    for (const countEl of this.querySelectorAll('[data-bundle-count]')) {
      countEl.textContent = String(selected.length);
    }

    const progress = this.querySelector('[data-bundle-progress]');
    if (progress instanceof HTMLElement && max) {
      progress.style.setProperty('--bundle-progress', `${Math.min(100, (selected.length / max) * 100)}%`);
    }

    // Slot previews for build-your-set.
    const slots = [...this.querySelectorAll('[data-bundle-slot]')];
    slots.forEach((slot, index) => {
      const item = selected[index];
      const img = item?.querySelector('[data-bundle-thumb]');
      slot.toggleAttribute('data-filled', Boolean(item));
      slot.replaceChildren();
      if (img instanceof HTMLImageElement) {
        const clone = img.cloneNode();
        clone.removeAttribute('data-bundle-thumb');
        clone.alt = '';
        clone.className = 'size-full object-contain';
        slot.append(clone);
      }
    });

    const ready = selected.length >= this.#min && (!this.dataset.exact || selected.length === max);
    for (const button of this.querySelectorAll('[data-bundle-submit]')) {
      if (button instanceof HTMLButtonElement) button.disabled = !ready || this.#busy;
    }
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this.#busy) return;

    const items = this.#selected()
      .map((item) => this.#variantFor(item).id)
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));
    if (!items.length) return;

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    this.#showError('');
    this.#update();

    try {
      const result = await addToCart(items);
      if (!result.ok) {
        if (result.message) {
          this.#showError(result.message);
        } else {
          // Network hiccup: fall back to the classic form post.
          const form = event.target;
          if (form instanceof HTMLFormElement) HTMLFormElement.prototype.submit.call(form);
        }
      } else {
        this.dispatchEvent(new CustomEvent('bundle:added', { bubbles: true }));
        // data-after-add="cart" (build-your-set catalogue) always lands on the cart page.
        if (this.dataset.afterAdd === 'cart' || !result.refreshed) {
          goToCart();
        } else {
          await openCartOrGo();
        }
      }
    } catch (error) {
      this.#showError(error?.message || this.dataset.errorMessage || '');
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      this.#update();
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-bundle-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = cents / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [whole, frac] = num.toFixed(decimals).split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${withSep}${decimal}${frac}` : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }
}

if (!customElements.get('s-bundle')) {
  customElements.define('s-bundle', SBundle);
}
